import type { SelectInputT, SimpleStyleT, TextI } from "tweenly"
import FormatAlignCenterRoundedIcon from "@mui/icons-material/FormatAlignCenterRounded"
import FormatAlignLeftRoundedIcon from "@mui/icons-material/FormatAlignLeftRounded"
import FormatAlignRightRoundedIcon from "@mui/icons-material/FormatAlignRightRounded"
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded"
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded"
import VerticalAlignBottomRoundedIcon from "@mui/icons-material/VerticalAlignBottomRounded"
import VerticalAlignCenterRoundedIcon from "@mui/icons-material/VerticalAlignCenterRounded"
import VerticalAlignTopRoundedIcon from "@mui/icons-material/VerticalAlignTopRounded"

import { SelectChangeEvent } from "@mui/material"
import { unionBy } from "lodash"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useCurrentCompanyStats } from "../../../../hooks/useCompanyStats"
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux"
import { setFontFamilyPreferencesAsync } from "../../../../store/slices/editor.slice"
import ExpandableSection from "../../../common/ExpandableSection"
import Box from "@mui/material/Box"
import ButtonGroupInput from "../../../common/PropertyTools/ButtonGroupInput"
import ButtonSwitch from "../../../common/PropertyTools/ButtonSwitch"
import ColorField from "../../../common/PropertyTools/ColorField"
import NumberInput from "../../../common/PropertyTools/NumberInput"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"
import SelectDividerInput from "../../../common/PropertyTools/SelectDividerInput"
import SelectInput from "../../../common/PropertyTools/SelectInput"
import { ColorEventT } from "../ExpandableSections/TextColor"
import { injectFontFaces } from "../../../../helpers/fontFace.helpers"
import { LoadFontButton } from "../LoadFontButton"

interface Props {
    tickerType: "scroll" | "crawl"
    textObjects: TextI[]
    handleSetStyleValue: (property: any, value: any) => void
}

const TickerFont = ({ textObjects, handleSetStyleValue, tickerType }: Props) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const object = textObjects[0] // show only first ... on change change same properties for every text

    const preferencesFontFamily: string[] = useAppSelector(
        (state) => state.editor.value.settings.fontFamily
    )

    const { assets: companyAssets } = useCurrentCompanyStats()
    const assets = useAppSelector((state) => state.assets.data)

    const fontFamily: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "fontFamily"
    )
    const fontSize: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "fontSize"
    )
    const fontWeight: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "fontWeight"
    )
    const fontStyle: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "fontStyle"
    )
    const textAlign: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "textAlign"
    )
    const textTransform: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "textTransform"
    )
    const justifyContent: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "justifyContent"
    )

    const color: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "color" || style.property === "backgroundColor"
    )
    const opacity: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "opacity"
    )

    const handleOnChangeColor = (e: ColorEventT) =>
        handleSetStyleValue(e.target.name, e.target.value)

    const fonts = useMemo(() => {
        return unionBy(
            assets.filter((asset) => asset.type === "font"),
            companyAssets.filter((asset) => asset.type === "font"),
            "_id"
        )
    }, [assets, companyAssets])

    useEffect(() => {
        injectFontFaces(fonts)
    }, [fonts])

    if (
        !fontFamily &&
        !fontSize &&
        !fontWeight &&
        !fontStyle &&
        !textAlign &&
        !textTransform &&
        !justifyContent
    )
        return <></>

    const handleStyleOnChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        handleSetStyleValue(e.target.name, e.target.value)

    const handleFontFamilyOnChange = (e: SelectChangeEvent<any>) => {
        handleSetStyleValue(fontFamily?.property, e.target.value)
        if ((fontFamily?.input as SelectInputT).props.includes(e.target.value)) {
            dispatch(setFontFamilyPreferencesAsync(e.target.value))
        }
    }

    // shown in %, like the object's own opacity in Appearance; stored as 0–1
    const opacityPercent = opacity ? Math.round(Number(opacity.value) * 100) : ""
    const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = String(e.target.value).trim()
        const percent = Number(text)
        if (text === "" || !Number.isFinite(percent) || percent === opacityPercent) return
        handleSetStyleValue("opacity", Math.max(0, Math.min(100, percent)) / 100)
    }

    return (
        <ExpandableSection
            expanded={false}
            title={"Font"}
            component={
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    width="100%"
                    pb={0.5}
                    // the theme gives every form control a 3px margin
                    sx={{ "& .MuiFormControl-root": { m: 0, width: "100%" } }}
                >
                    {fontFamily && (
                        <PropertyRow
                            gutter={
                                <LoadFontButton
                                    compact
                                    onChange={(value) =>
                                        handleSetStyleValue(fontFamily?.property, value)
                                    }
                                />
                            }
                        >
                            <SelectDividerInput
                                value={fontFamily.value}
                                label={fontFamily.input.label ?? fontFamily.property}
                                styleProperty={(fontFamily.input as SelectInputT).props}
                                stylePropertyBeforeDivider={[
                                    ...fonts.map((font) => font.name),
                                    ...preferencesFontFamily,
                                ]}
                                handleOnChange={handleFontFamilyOnChange}
                            />
                        </PropertyRow>
                    )}
                    {(fontSize || textTransform) && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    {fontSize && (
                                        <NumberInput
                                            styleProperty={fontSize}
                                            handleOnChange={handleStyleOnChange}
                                        />
                                    )}
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    {textTransform && (
                                        <SelectInput
                                            styleProperty={textTransform}
                                            handleOnChange={handleStyleOnChange}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </PropertyRow>
                    )}
                    {/* the same icon buttons, in the same rows, as a text's (see TextAndFont) */}
                    <PropertyRow>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Box display="flex" gap={1}>
                                <Box flex={3} minWidth={0} display="flex">
                                    {tickerType === "scroll" && textAlign && (
                                        <ButtonGroupInput
                                            buttons={[
                                                {
                                                    value: "left",
                                                    tooltip: t("editor:textTools.textAlign.left"),
                                                    icon: <FormatAlignLeftRoundedIcon />,
                                                },
                                                {
                                                    value: "center",
                                                    tooltip: t("editor:textTools.textAlign.center"),
                                                    icon: <FormatAlignCenterRoundedIcon />,
                                                },
                                                {
                                                    value: "right",
                                                    tooltip: t("editor:textTools.textAlign.right"),
                                                    icon: <FormatAlignRightRoundedIcon />,
                                                },
                                            ]}
                                            property={textAlign.property}
                                            value={textAlign.value}
                                            handleOnChange={handleSetStyleValue}
                                        />
                                    )}
                                </Box>
                                <Box flex={3} minWidth={0} display="flex">
                                    {justifyContent && (
                                        <ButtonGroupInput
                                            buttons={[
                                                {
                                                    value: "flex-start",
                                                    tooltip: t("editor:textTools.textAlign.top"),
                                                    icon: <VerticalAlignTopRoundedIcon />,
                                                },
                                                {
                                                    value: "center",
                                                    tooltip: t("editor:textTools.textAlign.center"),
                                                    icon: <VerticalAlignCenterRoundedIcon />,
                                                },
                                                {
                                                    value: "flex-end",
                                                    tooltip: t("editor:textTools.textAlign.bottom"),
                                                    icon: <VerticalAlignBottomRoundedIcon />,
                                                },
                                            ]}
                                            property={justifyContent.property}
                                            value={justifyContent.value}
                                            handleOnChange={handleSetStyleValue}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Box display="flex" gap={1}>
                                <Box flex={2} minWidth={0} display="flex" gap="3px">
                                    <Box flex={1} minWidth={0} display="flex">
                                        {fontWeight && (
                                            <ButtonSwitch
                                                selectedButton={{
                                                    value: "bold",
                                                    tooltip: t("editor:textTools.fontWeight.bold"),
                                                    icon: <FormatBoldRoundedIcon />,
                                                }}
                                                unselectedButton={{
                                                    value: "normal",
                                                    tooltip: t(
                                                        "editor:textTools.fontWeight.normal"
                                                    ),
                                                    icon: <FormatBoldRoundedIcon />,
                                                }}
                                                property={fontWeight.property}
                                                value={fontWeight.value}
                                                handleOnChange={handleSetStyleValue}
                                            />
                                        )}
                                    </Box>
                                    <Box flex={1} minWidth={0} display="flex">
                                        {fontStyle && (
                                            <ButtonSwitch
                                                selectedButton={{
                                                    value: "italic",
                                                    tooltip: t("editor:textTools.fontStyle.italic"),
                                                    icon: <FormatItalicRoundedIcon />,
                                                }}
                                                unselectedButton={{
                                                    value: "normal",
                                                    tooltip: t("editor:textTools.fontStyle.normal"),
                                                    icon: <FormatItalicRoundedIcon />,
                                                }}
                                                property={fontStyle.property}
                                                value={fontStyle.value}
                                                handleOnChange={handleSetStyleValue}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                <Box flex={4} />
                            </Box>
                        </Box>
                    </PropertyRow>
                    {opacity && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    <PrefixedInput
                                        name="opacity"
                                        value={opacityPercent}
                                        label="opacity"
                                        tooltip="Opacity (%)"
                                        handleOnChange={handleOpacityChange}
                                        min={0}
                                        max={100}
                                        endAdornment="%"
                                    />
                                </Box>
                                <Box flex={1} />
                            </Box>
                        </PropertyRow>
                    )}
                    {color && (
                        <PropertyRow>
                            <ColorField
                                styleProperty={color}
                                handleOnChange={handleOnChangeColor}
                            />
                        </PropertyRow>
                    )}
                </Box>
            }
        />
    )
}

export default TickerFont
